/**
 * Development-aware console wrapper.
 * Automatically strips logs in production builds (except errors).
 * 
 * @example
 * Console.log('Debug info', data);
 * Console.error('Something went wrong', error);
 */

const isDev = import.meta.env.DEV;

export const Console = {
  /**
   * Log general information (dev only)
   */
  log: (...args) => {
    if (isDev) console.log(...args);
  },

  /**
   * Log warnings (dev only)
   */
  warn: (...args) => {
    if (isDev) console.warn(...args);
  },

  /**
   * Log errors (always logged, even in production)
   */
  error: (...args) => {
    console.error(...args);
  },

  /**
   * Log informational messages (dev only)
   */
  info: (...args) => {
    if (isDev) console.info(...args);
  },

  /**
   * Log debug messages (dev only)
   */
  debug: (...args) => {
    if (isDev) console.debug(...args);
  },

  /**
   * Display data in table format (dev only)
   */
  table: (...args) => {
    if (isDev) console.table(...args);
  },

  /**
   * Create a new inline group (dev only)
   */
  group: (...args) => {
    if (isDev) console.group(...args);
  },

  /**
   * Create a new collapsed inline group (dev only)
   */
  groupCollapsed: (...args) => {
    if (isDev) console.groupCollapsed(...args);
  },

  /**
   * Exit the current inline group (dev only)
   */
  groupEnd: () => {
    if (isDev) console.groupEnd();
  },

  /**
   * Start a timer (dev only)
   */
  time: (label) => {
    if (isDev) console.time(label);
  },

  /**
   * Stop a timer (dev only)
   */
  timeEnd: (label) => {
    if (isDev) console.timeEnd(label);
  },

  /**
   * Log current time for a timer (dev only)
   */
  timeLog: (label, ...args) => {
    if (isDev) console.timeLog(label, ...args);
  },

  /**
   * Clear the console (dev only)
   */
  clear: () => {
    if (isDev) console.clear();
  },

  /**
   * Assert a condition and log if false (dev only)
   */
  assert: (condition, ...args) => {
    if (isDev) console.assert(condition, ...args);
  },

  /**
   * Count how many times this is called (dev only)
   */
  count: (label) => {
    if (isDev) console.count(label);
  },

  /**
   * Reset a counter (dev only)
   */
  countReset: (label) => {
    if (isDev) console.countReset(label);
  },

  /**
   * Log a stack trace (dev only)
   */
  trace: (...args) => {
    if (isDev) console.trace(...args);
  },
};
